import type { Workflow } from '@/pages/WorkflowsCatalog/types';
import type { FolderNode } from './WorkflowFolder';

// Helper to create a new folder node
function createFolderNode(name: string, path: string, level: number): FolderNode {
  return {
    name,
    path,
    workflows: [],
    subfolders: [],
    level,
    isFolder: true,
  };
}

// Helper to create a workflow node (non-folder item at root)
function createWorkflowNode(workflow: Workflow): FolderNode {
  return {
    name: workflow.title,
    path: workflow.functionName,
    workflows: [workflow],
    subfolders: [],
    level: 0,
    isFolder: false,
  };
}

// Check if a workflow should be at root level
function isRootLevelWorkflow(workflow: Workflow): boolean {
  // If no path, treat as root level
  if (!workflow.path) {
    return true;
  }

  // For Output API paths (file paths), treat all as root level to put in category
  // Output paths like "/app/dist/workflows/article_draft_agentic/workflow.js"
  // should be treated as simple category items
  if (workflow.path.startsWith('/app/dist/workflows/')) {
    return true;
  }

  // For Flow API, check if path has '/' (indicates nesting)
  return !workflow.path.includes('/');
}

// Get folder parts from workflow path (excluding the workflow name)
function getFolderParts(workflowPath: string): string[] {
  const pathParts = workflowPath.split('/');
  return pathParts.slice(0, -1);
}

// Add workflow to simple category folder (single-level path)
function addToRootCategory(workflow: Workflow, rootFolders: Record<string, FolderNode>): void {
  const categoryName = workflow.path.split('/')[0];

  if (!rootFolders[categoryName]) {
    rootFolders[categoryName] = createFolderNode(categoryName, categoryName, 0);
  }

  rootFolders[categoryName].workflows.push(workflow);
}

// Build nested folder structure for a workflow
function addToNestedFolders(
  workflow: Workflow,
  folderParts: string[],
  rootFolders: Record<string, FolderNode>
): void {
  let currentLevelMap = rootFolders;
  let currentNode: FolderNode | undefined;
  let accumulatedPath = '';

  folderParts.forEach((part, index) => {
    accumulatedPath = accumulatedPath ? `${accumulatedPath}/${part}` : part;

    // Create folder if it doesn't exist
    if (!currentLevelMap[part]) {
      currentLevelMap[part] = createFolderNode(part, accumulatedPath, index);
    }

    currentNode = currentLevelMap[part];

    // If this is the last folder part, add the workflow
    if (index === folderParts.length - 1) {
      currentNode.workflows.push(workflow);
    } else {
      // Prepare for next level
      currentLevelMap = getOrCreateNextLevel(
        currentNode,
        folderParts[index + 1],
        accumulatedPath,
        index
      );
    }
  });
}

// Get or create the next level map for nested folders
function getOrCreateNextLevel(
  parentNode: FolderNode,
  nextFolderName: string,
  currentPath: string,
  currentLevel: number
): Record<string, FolderNode> {
  // Create a map from existing subfolders
  const nextLevelMap: Record<string, FolderNode> = {};
  parentNode.subfolders.forEach((subfolder) => {
    nextLevelMap[subfolder.name] = subfolder;
  });

  // Create next folder if it doesn't exist
  if (!nextLevelMap[nextFolderName]) {
    const newSubfolder = createFolderNode(
      nextFolderName,
      `${currentPath}/${nextFolderName}`,
      currentLevel + 1
    );
    parentNode.subfolders.push(newSubfolder);
    nextLevelMap[nextFolderName] = newSubfolder;
  }

  return nextLevelMap;
}

// Apply sorting to all folders recursively
function applyRecursiveSorting(folders: FolderNode[]): void {
  folders.forEach((folder) => {
    // Sort workflows alphabetically
    folder.workflows = sortWorkflowsByTitle(folder.workflows);

    // Sort subfolders: files first, then alphabetically
    if (folder.subfolders.length > 0) {
      folder.subfolders.sort(sortFilesFirst);
      applyRecursiveSorting(folder.subfolders);
    }
  });
}

// Main function to build folder structure
export function buildFolderStructure(workflows: Workflow[]): FolderNode[] {
  const rootItems: FolderNode[] = [];
  const rootFolders: Record<string, FolderNode> = {};
  const rootWorkflows: Workflow[] = [];

  // Categorize workflows
  workflows.forEach((workflow) => {
    if (isRootLevelWorkflow(workflow)) {
      rootWorkflows.push(workflow);
      return;
    }

    const folderParts = getFolderParts(workflow.path);

    if (folderParts.length === 0) {
      // Single-level path (e.g., "category/workflow")
      addToRootCategory(workflow, rootFolders);
    } else {
      // Multi-level path (e.g., "category/subcategory/workflow")
      addToNestedFolders(workflow, folderParts, rootFolders);
    }
  });

  // Create nodes for root-level workflows
  rootWorkflows.forEach((workflow) => {
    rootItems.push(createWorkflowNode(workflow));
  });

  // Add all folders to root items
  Object.values(rootFolders).forEach((folder) => {
    rootItems.push(folder);
  });

  // Apply sorting to all items
  applyRecursiveSorting(rootItems);

  // Sort root items: files first, then folders alphabetically
  return rootItems.sort(sortFilesFirst);
}

export function filterWorkflowsInFolder(
  folder: FolderNode,
  searchQuery: string
): FolderNode | null {
  if (!searchQuery.trim()) {
    return folder;
  }

  const query = searchQuery.toLowerCase().trim();
  const filteredWorkflows = folder.workflows.filter(
    (workflow) =>
      (workflow.title && workflow.title.toLowerCase().includes(query)) ||
      (workflow.description && workflow.description.toLowerCase().includes(query))
  );

  // For non-folder items (individual workflows at root), just check if they match
  if (!folder.isFolder) {
    return filteredWorkflows.length > 0
      ? {
          ...folder,
          workflows: filteredWorkflows,
        }
      : null;
  }

  const filteredSubfolders = folder.subfolders
    .map((subfolder) => filterWorkflowsInFolder(subfolder, searchQuery))
    .filter(Boolean) as FolderNode[];

  // If this folder has no matching workflows and no matching subfolders, return null
  if (filteredWorkflows.length === 0 && filteredSubfolders.length === 0) {
    return null;
  }

  return {
    ...folder,
    workflows: filteredWorkflows,
    subfolders: filteredSubfolders,
  };
}

export function countWorkflowsInFolder(folder: FolderNode): number {
  const directWorkflows = folder.workflows.length;
  const subfoldersWorkflows = folder.subfolders.reduce(
    (sum, subfolder) => sum + countWorkflowsInFolder(subfolder),
    0
  );
  return directWorkflows + subfoldersWorkflows;
}

// Helper to collect all folder paths recursively
export function collectAllFolderPaths(folders: FolderNode[]): Set<string> {
  const paths = new Set<string>();

  const collectPaths = (folderList: FolderNode[]) => {
    folderList.forEach((folder) => {
      if (folder.isFolder) {
        paths.add(folder.path);
        if (folder.subfolders && folder.subfolders.length > 0) {
          collectPaths(folder.subfolders);
        }
      }
    });
  };

  collectPaths(folders);
  return paths;
}

// Sort function for files first, then folders
export function sortFilesFirst(a: FolderNode, b: FolderNode): number {
  if (!a.isFolder && b.isFolder) return -1;
  if (a.isFolder && !b.isFolder) return 1;
  return a.name.localeCompare(b.name);
}

// Sort workflows alphabetically
export function sortWorkflowsByTitle(workflows: Workflow[]): Workflow[] {
  return workflows.sort((a, b) => a.title.localeCompare(b.title));
}
